import { fetchVaultLinks, fetchVaultPages, createVaultRun, updateVaultRun, upsertVaultPage } from '@/lib/librarian/api';
import { initializeVault } from '@/lib/librarian/initializeVault';
import { updateIndexPage } from '@/lib/librarian/updateIndexPage';
import { updateLogPage } from '@/lib/librarian/updateLogPage';
import type { KnowledgeBaseVaultPage, LibrarianLintResult } from '@/lib/librarian/types';

const STALE_DAYS = 30;

export async function lintVault(knowledgeBaseId: string, userId?: string | null): Promise<LibrarianLintResult> {
  const vault = await initializeVault(knowledgeBaseId);
  const run = await createVaultRun({
    vaultId: vault.id,
    runType: 'lint',
    status: 'running',
    createdBy: userId ?? null,
    notes: 'Running librarian vault maintenance checks.',
  });

  const pages = await fetchVaultPages(vault.id);
  const links = await fetchVaultLinks(vault.id);
  const now = Date.now();
  const inboundCounts = new Map<string, number>();
  const outboundCounts = new Map<string, number>();

  for (const link of links) {
    outboundCounts.set(link.from_page_id, (outboundCounts.get(link.from_page_id) ?? 0) + 1);
    if (link.to_page_id) {
      inboundCounts.set(link.to_page_id, (inboundCounts.get(link.to_page_id) ?? 0) + 1);
    }
  }

  const orphanPages = pages.filter((page) => {
    const isRootUtility = ['index.md', 'log.md', 'overview.md'].includes(page.path);
    if (isRootUtility) return false;
    return (inboundCounts.get(page.id) ?? 0) === 0;
  });

  const stalePages = pages.filter((page) => now - new Date(page.updated_at).getTime() > STALE_DAYS * 24 * 60 * 60 * 1000);
  const missingCrossLinks = pages.filter(
    (page) => page.page_type !== 'log' && (outboundCounts.get(page.id) ?? 0) === 0 && page.path !== 'index.md'
  );

  const suggestions = [
    orphanPages.length > 0 ? `Add inbound links to ${orphanPages.length} orphan page(s).` : 'No orphan pages detected.',
    stalePages.length > 0 ? `Refresh ${stalePages.length} stale page(s) that have not been updated in ${STALE_DAYS}+ days.` : 'No stale pages detected.',
    missingCrossLinks.length > 0 ? `Add cross-links from ${missingCrossLinks.length} page(s) with no outbound references.` : 'Cross-link coverage looks healthy.',
  ];

  const reportMarkdown = [
    '# Librarian Lint Report',
    '',
    `Generated: ${new Date().toISOString()}`,
    '',
    '## Orphan Pages',
    ...(orphanPages.length > 0 ? orphanPages.map((page) => `- [${page.title}](${page.path})`) : ['- None']),
    '',
    '## Stale Pages',
    ...(stalePages.length > 0 ? stalePages.map((page) => `- [${page.title}](${page.path}) · updated ${page.updated_at}`) : ['- None']),
    '',
    '## Missing Cross-links',
    ...(missingCrossLinks.length > 0 ? missingCrossLinks.map((page) => `- [${page.title}](${page.path})`) : ['- None']),
    '',
    '## Suggestions',
    ...suggestions.map((suggestion) => `- ${suggestion}`),
  ].join('\n');

  const reportPage = await upsertVaultPage({
    vaultId: vault.id,
    path: `syntheses/lint-report-${new Date().toISOString().slice(0, 10)}.md`,
    title: 'Lint Report',
    pageType: 'lint-report',
    contentMarkdown: reportMarkdown,
    summary: 'Most recent librarian vault lint report.',
    metadata: {
      orphanCount: orphanPages.length,
      staleCount: stalePages.length,
      missingCrossLinksCount: missingCrossLinks.length,
    },
  });

  await updateIndexPage(vault);
  await updateLogPage(vault, `Ran vault lint and generated report ${reportPage.path}.`);

  const completedRun = await updateVaultRun(run.id, {
    status: 'completed',
    outputPageIds: [reportPage.id],
    notes: suggestions.join(' '),
  });

  return {
    run: completedRun,
    reportPage,
    orphanPages,
    stalePages,
    missingCrossLinks,
    suggestions,
  };
}
